import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { FormCategoryService } from '../form-category.service';

export const useCreateFormCategory = (
	onSuccess?: (category: Forms.FormCategoryCatalog) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Forms.CreateFormCategory) =>
			FormCategoryService.create(body),
		onSuccess: (category) => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			toast.success('Categoria criada com sucesso!');
			if (onSuccess) onSuccess(category);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar categoria'));
		},
	});
};

export const usePatchFormCategory = (
	onSuccess?: (category: Forms.FormCategoryCatalog) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Forms.UpdateFormCategory;
		}) => FormCategoryService.patch(id, body),
		onSuccess: (category, variables) => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			queryClient.invalidateQueries({
				queryKey: ['form-category', variables.id],
			});
			toast.success('Categoria atualizada com sucesso!');
			if (onSuccess) onSuccess(category);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar categoria'));
		},
	});
};

export const useRemoveFormCategory = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => FormCategoryService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			toast.success('Categoria removida com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover categoria'));
		},
	});
};

export const useCreateFormSubcategory = (
	onSuccess?: (subcategory: Forms.FormSubcategoryCatalog) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			categoryId,
			body,
		}: {
			categoryId: string;
			body: Forms.CreateFormSubcategory;
		}) => FormCategoryService.addSubcategory(categoryId, body),
		onSuccess: (subcategory, variables) => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			queryClient.invalidateQueries({
				queryKey: ['form-category', variables.categoryId],
			});
			toast.success('Subcategoria criada com sucesso!');
			if (onSuccess) onSuccess(subcategory);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar subcategoria'));
		},
	});
};

export const usePatchFormSubcategory = (
	onSuccess?: (subcategory: Forms.FormSubcategoryCatalog) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			categoryId,
			subcategoryId,
			body,
		}: {
			categoryId: string;
			subcategoryId: string;
			body: Forms.UpdateFormSubcategory;
		}) =>
			FormCategoryService.patchSubcategory(
				categoryId,
				subcategoryId,
				body,
			),
		onSuccess: (subcategory, variables) => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			queryClient.invalidateQueries({
				queryKey: ['form-category', variables.categoryId],
			});
			toast.success('Subcategoria atualizada com sucesso!');
			if (onSuccess) onSuccess(subcategory);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao atualizar subcategoria'),
			);
		},
	});
};

export const useRemoveFormSubcategory = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			categoryId,
			subcategoryId,
		}: {
			categoryId: string;
			subcategoryId: string;
		}) => FormCategoryService.removeSubcategory(categoryId, subcategoryId),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['form-categories'] });
			queryClient.invalidateQueries({
				queryKey: ['form-category', variables.categoryId],
			});
			toast.success('Subcategoria removida com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover subcategoria'));
		},
	});
};
